import { useState, useEffect } from 'react'
import { Plus, MapPin, Hash, Edit, Trash2, X, Building2, Sparkles, Navigation, Compass, Shield, Info, Users, Layers } from 'lucide-react'
import { centersAPI, districtsAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'
import { parsePaginatedResponse } from '../../lib/pagination'
import Pagination from '../../components/Pagination'
import GeofencePolicyCard from '../../components/GeofencePolicyCard'

export default function CentersPage() {
  const { success, error: toastError } = useToast()
  const [tab, setTab] = useState<'centers' | 'districts'>('centers')

  const [centers, setCenters] = useState<any[]>([])
  const [districts, setDistricts] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const [centersPage, setCentersPage] = useState(1)
  const [centersCount, setCentersCount] = useState(0)
  const [centersHasNext, setCentersHasNext] = useState(false)
  const [centersHasPrev, setCentersHasPrev] = useState(false)

  const [districtsPage, setDistrictsPage] = useState(1)
  const [districtsCount, setDistrictsCount] = useState(0)
  const [districtsHasNext, setDistrictsHasNext] = useState(false)
  const [districtsHasPrev, setDistrictsHasPrev] = useState(false)

  const [districtCenterCounts, setDistrictCenterCounts] = useState<Record<number, number>>({})

  const [showAddModal, setShowAddModal] = useState(false)
  const [editingCenter, setEditingCenter] = useState<any | null>(null)
  const [form, setForm] = useState({
    name: '', code: '', district: '', latitude: '', longitude: '', geofence_radius_m: '',
  })
  const [centerErrors, setCenterErrors] = useState({
    name: '', code: '', district: '', latitude: '', longitude: '', geofence_radius_m: ''
  })

  const [showDistrictModal, setShowDistrictModal] = useState(false)
  const [editingDistrict, setEditingDistrict] = useState<any | null>(null)
  const [districtForm, setDistrictForm] = useState({ name: '', code: '', latitude: '', longitude: '' })
  const [districtErrors, setDistrictErrors] = useState({
    name: '', code: '', latitude: '', longitude: ''
  })

  const [isSaving, setIsSaving] = useState(false)

  const loadData = async (centersPageArg = centersPage, districtsPageArg = districtsPage) => {
    setIsLoading(true)
    try {
      const [centersRes, districtsRes] = await Promise.all([
        centersAPI.list(centersPageArg),
        districtsAPI.list(districtsPageArg),
      ])
      const centersParsed = parsePaginatedResponse(centersRes.data)
      const districtsParsed = parsePaginatedResponse(districtsRes.data)
      setCenters(centersParsed.results)
      setCentersCount(centersParsed.count)
      setCentersHasNext(centersParsed.hasNext)
      setCentersHasPrev(centersParsed.hasPrev)
      setDistricts(districtsParsed.results)
      setDistrictsCount(districtsParsed.count)
      setDistrictsHasNext(districtsParsed.hasNext)
      setDistrictsHasPrev(districtsParsed.hasPrev)

      const counts: Record<number, number> = {}
      await Promise.all(
        districtsParsed.results.map(async (d: any) => {
          try {
            const res = await centersAPI.list(1, { district: d.id })
            counts[d.id] = res.data.count ?? (Array.isArray(res.data) ? res.data.length : 0)
          } catch {
            counts[d.id] = 0
          }
        })
      )
      setDistrictCenterCounts((prev) => ({ ...prev, ...counts }))
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load training centers.'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadData(centersPage, districtsPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centersPage, districtsPage])

  const openAddModal = () => {
    setEditingCenter(null)
    setForm({ name: '', code: '', district: '', latitude: '', longitude: '', geofence_radius_m: '' })
    setCenterErrors({ name: '', code: '', district: '', latitude: '', longitude: '', geofence_radius_m: '' })
    setShowAddModal(true)
  }

  const openEditModal = (center: any) => {
    setEditingCenter(center)
    setForm({
      name: center.name,
      code: center.code,
      district: String(center.district),
      latitude: String(center.latitude),
      longitude: String(center.longitude),
      geofence_radius_m: center.geofence_radius_m ? String(center.geofence_radius_m) : '',
    })
    setCenterErrors({ name: '', code: '', district: '', latitude: '', longitude: '', geofence_radius_m: '' })
    setShowAddModal(true)
  }

  const validateCenterForm = () => {
    const errors = {
      name: '',
      code: '',
      district: '',
      latitude: '',
      longitude: '',
      geofence_radius_m: ''
    }
    let isValid = true

    if (!form.name || form.name.trim() === '') {
      errors.name = 'Center name is required.'
      isValid = false
    }

    if (!form.code || form.code.trim() === '') {
      errors.code = 'Center code is required.'
      isValid = false
    }

    if (!form.district || form.district.trim() === '') {
      errors.district = 'Please select a district.'
      isValid = false
    }

    if (!form.latitude || form.latitude.trim() === '') {
      errors.latitude = 'Latitude is required.'
      isValid = false
    } else if (isNaN(Number(form.latitude)) || Number(form.latitude) < -90 || Number(form.latitude) > 90) {
      errors.latitude = 'Please enter a valid latitude (-90 to 90).'
      isValid = false
    }

    if (!form.longitude || form.longitude.trim() === '') {
      errors.longitude = 'Longitude is required.'
      isValid = false
    } else if (isNaN(Number(form.longitude)) || Number(form.longitude) < -180 || Number(form.longitude) > 180) {
      errors.longitude = 'Please enter a valid longitude (-180 to 180).'
      isValid = false
    }

    if (form.geofence_radius_m && form.geofence_radius_m.trim() !== '') {
      if (isNaN(Number(form.geofence_radius_m)) || Number(form.geofence_radius_m) < 0) {
        errors.geofence_radius_m = 'Please enter a valid positive number.'
        isValid = false
      }
    }

    setCenterErrors(errors)
    return isValid
  }

  const handleSave = async () => {
    if (!validateCenterForm()) return

    setIsSaving(true)
    try {
      const payload: any = {
        name: form.name,
        code: form.code,
        district: Number(form.district),
        latitude: form.latitude,
        longitude: form.longitude,
      }
      if (form.geofence_radius_m) payload.geofence_radius_m = Number(form.geofence_radius_m)

      if (editingCenter) {
        await centersAPI.update(editingCenter.id, payload)
        success('Training center updated successfully.')
      } else {
        await centersAPI.create(payload)
        success('Training center added successfully.')
      }
      setShowAddModal(false)
      setCenterErrors({ name: '', code: '', district: '', latitude: '', longitude: '', geofence_radius_m: '' })
      await loadData()
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to save training center.'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this training center?')) return
    try {
      await centersAPI.delete(id)
      success('Training center deleted.')
      await loadData()
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to delete training center.'))
    }
  }

  const openAddDistrict = () => {
    setEditingDistrict(null)
    setDistrictForm({ name: '', code: '', latitude: '', longitude: '' })
    setDistrictErrors({ name: '', code: '', latitude: '', longitude: '' })
    setShowDistrictModal(true)
  }

  const openEditDistrict = (district: any) => {
    setEditingDistrict(district)
    setDistrictForm({
      name: district.name,
      code: district.code,
      latitude: district.latitude ? String(district.latitude) : '',
      longitude: district.longitude ? String(district.longitude) : '',
    })
    setDistrictErrors({ name: '', code: '', latitude: '', longitude: '' })
    setShowDistrictModal(true)
  }

  const validateDistrictForm = () => {
    const errors = { name: '', code: '', latitude: '', longitude: '' }
    let isValid = true

    if (!districtForm.name || districtForm.name.trim() === '') {
      errors.name = 'District name is required.'
      isValid = false
    }

    if (!districtForm.code || districtForm.code.trim() === '') {
      errors.code = 'District code is required.'
      isValid = false
    }

    if (districtForm.latitude && districtForm.latitude.trim() !== '') {
      if (isNaN(Number(districtForm.latitude)) || Number(districtForm.latitude) < -90 || Number(districtForm.latitude) > 90) {
        errors.latitude = 'Please enter a valid latitude (-90 to 90).'
        isValid = false
      }
    }

    if (districtForm.longitude && districtForm.longitude.trim() !== '') {
      if (isNaN(Number(districtForm.longitude)) || Number(districtForm.longitude) < -180 || Number(districtForm.longitude) > 180) {
        errors.longitude = 'Please enter a valid longitude (-180 to 180).'
        isValid = false
      }
    }

    setDistrictErrors(errors)
    return isValid
  }

  const handleSaveDistrict = async () => {
    if (!validateDistrictForm()) return

    setIsSaving(true)
    try {
      const payload: any = {
        name: districtForm.name,
        code: districtForm.code
      }
      if (districtForm.latitude && districtForm.latitude.trim() !== '') {
        payload.latitude = districtForm.latitude
      }
      if (districtForm.longitude && districtForm.longitude.trim() !== '') {
        payload.longitude = districtForm.longitude
      }

      if (editingDistrict) {
        await districtsAPI.update(editingDistrict.id, payload)
        success('District updated successfully.')
      } else {
        await districtsAPI.create(payload)
        success('District added successfully.')
      }
      setShowDistrictModal(false)
      setDistrictErrors({ name: '', code: '', latitude: '', longitude: '' })
      await loadData()
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to save district.'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteDistrict = async (id: string) => {
    if (!confirm('Delete this district? Training centres assigned to it must be reassigned or removed first.')) return
    try {
      await districtsAPI.delete(id)
      success('District deleted.')
      await loadData()
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to delete district. It may still have training centres assigned.'))
    }
  }

  const inputClass = "w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
  const errorInputClass = "w-full px-3.5 py-2.5 bg-red-50/50 border border-red-400 rounded-xl text-sm text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none transition-all"

  const tabs = [
    { key: 'centers' as const, label: 'Training Centers', icon: MapPin, count: centersCount },
    { key: 'districts' as const, label: 'Districts', icon: Building2, count: districtsCount },
  ]

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            Administration
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Locations
          </h1>
          <p className="text-sm text-neutral-500">Manage districts and IRCS training centers</p>
        </div>
        <button
          onClick={tab === 'centers' ? openAddModal : openAddDistrict}
          className="group flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium transition-all duration-300 shadow-md shadow-[#A31621]/20 hover:shadow-lg hover:shadow-[#A31621]/30 hover:-translate-y-0.5 self-start"
        >
          <Plus className="w-4 h-4 transition-transform group-hover:rotate-90" />
          {tab === 'centers' ? 'Add Center' : 'Add District'}
        </button>
      </div>

      {/* Geofence Policy Card — component keeps its own styling */}
      <GeofencePolicyCard />

      {/* ── Tabs ── */}
      <div className="mb-6 flex items-center gap-1.5 bg-white border border-neutral-200 rounded-2xl p-1.5 shadow-sm w-fit">
        {tabs.map(({ key, label, icon: Icon, count }) => {
          const active = tab === key
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`group flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                active
                  ? 'bg-gradient-to-r from-[#F0821F] to-[#E4670F] text-white shadow-md shadow-[#E4670F]/25'
                  : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
              }`}
            >
              <Icon
                className={`w-4 h-4 transition-transform ${
                  !active ? 'group-hover:scale-110' : ''
                }`}
              />
              {label}
              <span
                className={`ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-md text-[10px] font-bold tabular-nums ${
                  active
                    ? 'bg-white/25 text-white'
                    : 'bg-neutral-100 text-neutral-600'
                }`}
              >
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {isLoading ? (
        <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
          <p className="text-sm text-neutral-500">Loading...</p>
        </div>
      ) : tab === 'centers' ? (
        centers.length === 0 ? (
          <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center">
              <MapPin className="w-8 h-8 text-neutral-400" />
            </div>
            <p className="text-sm font-medium text-neutral-700">No training centers found</p>
            <p className="text-xs text-neutral-400">Add one above to get started.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {centers.map((center) => (
                <div
                  key={center.id}
                  className="group relative bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden hover:shadow-lg hover:shadow-neutral-200/50 hover:ring-neutral-300/70 transition-all duration-300"
                >
                  {/* Top accent */}
                  <div className="h-1 w-full bg-gradient-to-r from-[#F0821F] to-[#A31621]" />

                  <div className="p-5">
                    {/* Icon + actions */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEditModal(center)}
                          title="Edit"
                          className="p-2 text-neutral-500 hover:text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-lg transition-all"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(center.id)}
                          title="Delete"
                          className="p-2 text-neutral-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Name + district */}
                    <h3 className="text-base font-bold text-neutral-900 mb-1 truncate" title={center.name}>
                      {center.name}
                    </h3>
                    <p className="inline-flex items-center gap-1.5 text-xs text-neutral-500 mb-4">
                      <Building2 className="w-3 h-3" />
                      {center.district_name} District
                    </p>

                    {/* Info rows */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2.5 bg-neutral-50 rounded-lg px-3 py-2">
                        <Hash className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                        <span className="text-xs font-medium text-neutral-700 tabular-nums truncate">{center.code}</span>
                      </div>
                      <div className="flex items-center gap-2.5 bg-neutral-50 rounded-lg px-3 py-2">
                        <Compass className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                        <span className="text-xs font-medium text-neutral-700 tabular-nums truncate">
                          {Number(center.latitude).toFixed(4)}, {Number(center.longitude).toFixed(4)}
                        </span>
                      </div>
                    </div>

                    {/* Geofence footer */}
                    <div className="mt-4 pt-3.5 border-t border-neutral-100 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Shield className="w-3.5 h-3.5 text-[#C24A0A]" />
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                          Geofence
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-neutral-800 tabular-nums">
                        {center.geofence_radius_m ? `${center.geofence_radius_m} m` : 'Default'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Pagination page={centersPage} onPageChange={setCentersPage} count={centersCount} hasNext={centersHasNext} hasPrev={centersHasPrev} />
          </>
        )
      ) : districts.length === 0 ? (
        <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-neutral-400" />
          </div>
          <p className="text-sm font-medium text-neutral-700">No districts found</p>
          <p className="text-xs text-neutral-400">Add one above to get started.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50/80 border-b border-neutral-200/70">
                  <tr>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Name</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Code</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Centers</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Coordinates</th>
                    <th className="px-5 py-3.5 text-right text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {districts.map((d) => (
                    <tr
                      key={d.id}
                      className="border-b border-neutral-100 last:border-b-0 hover:bg-[#FAF6EE]/60 transition-colors"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-sm flex-shrink-0">
                            <Building2 className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-semibold text-neutral-900">{d.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-700 bg-neutral-100 rounded-md px-2 py-0.5 tabular-nums">
                          <Hash className="w-3 h-3 text-neutral-400" />
                          {d.code}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 text-sm text-neutral-700 tabular-nums">
                          <MapPin className="w-3.5 h-3.5 text-neutral-400" />
                          <span className="font-semibold">{districtCenterCounts[d.id] ?? 0}</span>
                          <span className="text-xs text-neutral-500">
                            {districtCenterCounts[d.id] === 1 ? 'center' : 'centers'}
                          </span>
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {d.latitude && d.longitude ? (
                          <span className="inline-flex items-center gap-1.5 text-sm text-neutral-700 tabular-nums">
                            <Compass className="w-3.5 h-3.5 text-neutral-400" />
                            {Number(d.latitude).toFixed(4)}, {Number(d.longitude).toFixed(4)}
                          </span>
                        ) : (
                          <span className="text-sm text-neutral-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => openEditDistrict(d)}
                            title="Edit"
                            className="p-2 text-neutral-500 hover:text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-lg transition-all"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDistrict(d.id)}
                            title="Delete"
                            className="p-2 text-neutral-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination page={districtsPage} onPageChange={setDistrictsPage} count={districtsCount} hasNext={districtsHasNext} hasPrev={districtsHasPrev} />
        </>
      )}

      {/* ── Training Centre Modal ── */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
                  {editingCenter ? <Edit className="w-4 h-4 text-white" /> : <MapPin className="w-4 h-4 text-white" />}
                </div>
                <h2 className="text-lg font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  {editingCenter ? 'Edit Training Center' : 'Add Training Center'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setCenterErrors({ name: '', code: '', district: '', latitude: '', longitude: '', geofence_radius_m: '' })
                }}
                className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                  Name <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <input
                    type="text"
                    placeholder="e.g. IRCS Training Centre Gurugram"
                    value={form.name}
                    onChange={(e) => {
                      setForm({ ...form, name: e.target.value })
                      if (centerErrors.name) setCenterErrors({ ...centerErrors, name: '' })
                    }}
                    className={`pl-10 ${centerErrors.name ? errorInputClass : inputClass}`}
                  />
                </div>
                {centerErrors.name && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {centerErrors.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                  Code <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <Hash className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <input
                    type="text"
                    placeholder="e.g. IRCS-GGN-01"
                    value={form.code}
                    onChange={(e) => {
                      setForm({ ...form, code: e.target.value })
                      if (centerErrors.code) setCenterErrors({ ...centerErrors, code: '' })
                    }}
                    className={`pl-10 ${centerErrors.code ? errorInputClass : inputClass}`}
                  />
                </div>
                {centerErrors.code && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {centerErrors.code}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                  District <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <select
                    value={form.district}
                    onChange={(e) => {
                      setForm({ ...form, district: e.target.value })
                      if (centerErrors.district) setCenterErrors({ ...centerErrors, district: '' })
                    }}
                    className={`pl-10 pr-8 appearance-none cursor-pointer ${centerErrors.district ? errorInputClass : inputClass}`}
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
                {centerErrors.district && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {centerErrors.district}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                    Latitude <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="28.4595"
                    value={form.latitude}
                    onChange={(e) => {
                      setForm({ ...form, latitude: e.target.value })
                      if (centerErrors.latitude) setCenterErrors({ ...centerErrors, latitude: '' })
                    }}
                    className={`tabular-nums ${centerErrors.latitude ? errorInputClass : inputClass}`}
                  />
                  {centerErrors.latitude && (
                    <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {centerErrors.latitude}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                    Longitude <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="77.0266"
                    value={form.longitude}
                    onChange={(e) => {
                      setForm({ ...form, longitude: e.target.value })
                      if (centerErrors.longitude) setCenterErrors({ ...centerErrors, longitude: '' })
                    }}
                    className={`tabular-nums ${centerErrors.longitude ? errorInputClass : inputClass}`}
                  />
                  {centerErrors.longitude && (
                    <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {centerErrors.longitude}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                  Geofence Radius (m)
                  <span className="text-neutral-400 normal-case font-normal ml-1.5">optional</span>
                </label>
                <div className="relative group">
                  <Shield className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <input
                    type="number"
                    min="0"
                    placeholder="Leave blank to use the default policy radius"
                    value={form.geofence_radius_m}
                    onChange={(e) => {
                      setForm({ ...form, geofence_radius_m: e.target.value })
                      if (centerErrors.geofence_radius_m) setCenterErrors({ ...centerErrors, geofence_radius_m: '' })
                    }}
                    className={`pl-10 tabular-nums ${centerErrors.geofence_radius_m ? errorInputClass : inputClass}`}
                  />
                </div>
                {centerErrors.geofence_radius_m && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {centerErrors.geofence_radius_m}
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50/60">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setCenterErrors({ name: '', code: '', district: '', latitude: '', longitude: '', geofence_radius_m: '' })
                }}
                className="flex-1 px-4 py-2.5 border border-neutral-200 bg-white rounded-xl font-medium text-neutral-700 hover:bg-neutral-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium disabled:opacity-50 transition-all shadow-md shadow-[#A31621]/20 hover:shadow-lg"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── District Modal ── */}
      {showDistrictModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
                  {editingDistrict ? <Edit className="w-4 h-4 text-white" /> : <Building2 className="w-4 h-4 text-white" />}
                </div>
                <h2 className="text-lg font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  {editingDistrict ? 'Edit District' : 'Add District'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowDistrictModal(false)
                  setDistrictErrors({ name: '', code: '', latitude: '', longitude: '' })
                }}
                className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                  District Name <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <input
                    type="text"
                    placeholder="e.g. Gurugram"
                    value={districtForm.name}
                    onChange={(e) => {
                      setDistrictForm({ ...districtForm, name: e.target.value })
                      if (districtErrors.name) setDistrictErrors({ ...districtErrors, name: '' })
                    }}
                    className={`pl-10 ${districtErrors.name ? errorInputClass : inputClass}`}
                  />
                </div>
                {districtErrors.name && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {districtErrors.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                  Code <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <Hash className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <input
                    type="text"
                    placeholder="e.g. GGN"
                    value={districtForm.code}
                    onChange={(e) => {
                      setDistrictForm({ ...districtForm, code: e.target.value })
                      if (districtErrors.code) setDistrictErrors({ ...districtErrors, code: '' })
                    }}
                    className={`pl-10 ${districtErrors.code ? errorInputClass : inputClass}`}
                  />
                </div>
                {districtErrors.code && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {districtErrors.code}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                  Coordinates
                  <span className="text-neutral-400 normal-case font-normal ml-1.5">optional</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <input
                      type="text"
                      placeholder="Latitude"
                      value={districtForm.latitude}
                      onChange={(e) => {
                        setDistrictForm({ ...districtForm, latitude: e.target.value })
                        if (districtErrors.latitude) setDistrictErrors({ ...districtErrors, latitude: '' })
                      }}
                      className={`tabular-nums ${districtErrors.latitude ? errorInputClass : inputClass}`}
                    />
                    {districtErrors.latitude && (
                      <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-red-500" />
                        {districtErrors.latitude}
                      </p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Longitude"
                      value={districtForm.longitude}
                      onChange={(e) => {
                        setDistrictForm({ ...districtForm, longitude: e.target.value })
                        if (districtErrors.longitude) setDistrictErrors({ ...districtErrors, longitude: '' })
                      }}
                      className={`tabular-nums ${districtErrors.longitude ? errorInputClass : inputClass}`}
                    />
                    {districtErrors.longitude && (
                      <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-red-500" />
                        {districtErrors.longitude}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50/60">
              <button
                onClick={() => {
                  setShowDistrictModal(false)
                  setDistrictErrors({ name: '', code: '', latitude: '', longitude: '' })
                }}
                className="flex-1 px-4 py-2.5 border border-neutral-200 bg-white rounded-xl font-medium text-neutral-700 hover:bg-neutral-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDistrict}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium disabled:opacity-50 transition-all shadow-md shadow-[#A31621]/20 hover:shadow-lg"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}